import {
  timer,
} from '@homeservices/tool-kit/proreferral/utils/time-formats';

<template>
  <div v-show="showSubStatus">
    <router-link
        :to="{
          name: 'dashboard::pro::jobs',
          params: {
            jobId: job.order_hash,
          },
          query: $route.query,
        }"
        class="JOB-LIST-ITEM"
        active-class="ACTIVE">
      <div class="d-flex flex-0-0 fd-column">
        <span class="NOTIFICATION-BUBBLE" :class="{ 'ACTIVE': hasNotification }" />
      </div>
      <div class="d-flex flex-1-1 fd-column ov-hidden p-r-1">
        <div class="d-flex jc-between">
          <job-list-item-details
              :full-name="job.location.first_name | fullName(job.location.last_name)"
              :serviceName="job.service_name"
              :location="job.location | location"
          />
          <div class="d-flex flex-0-0 fd-column ai-end text-right">
            <span class="text-md DARK-TEXT" selenium="current-job-time-label">
              {{ new Date(time) | timeReceivedJobs }}
            </span>
            <span
                v-if="isOverdue"
                class="OVERDUE_BUBBLE"
                selenium="current-job-timer">
              <vk-icon name="simple-clock" class="ICON" />
              <span>Overdue</span>
            </span>
            <span
                v-else-if="$options.JOBS_STATUS_ALIASES.new.statuses.includes(match.status)"
                v-tooltip="{
                  content: 'New leads expire in 48 hours. Reply before it’s gone!',
                  trigger: 'click hover',
                }"
                class="TIMER"
                selenium="current-job-timer">
              <vk-icon name="simple-clock" class="ICON" />
              {{ new Date(timeToExpire) | timer }}
            </span>
            <status-bubble
                v-if="isPastOrSearchStatusAlias"
                :status="match.status"
                class="STATUS-BUBBLE"
            />
          </div>
        </div>
        <div class="d-flex">
          <green-bubble
              v-if="isReadyToHire"
              :bubbleText="urgency"
              class= "m-r-1"/>
          <transition v-if="isPotentialMatch" name="FADE" mode="out-in">
            <sub-status-bubble
                v-if="!!match.sub_status"
                :status="match.sub_status"
                class="STATUS-BUBBLE m-r-1"
            />
          </transition>
          <transition name="FADE" mode="out-in">
            <appointment-bubble
                v-if="hasAppointment"
                :purpose="appointments[0].purpose || ''"
                :purposeDescription="appointments[0].purpose_description || ''"
                :active="hasUpcomingAppointment" />
          </transition>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script>
import { isAfter } from 'date-fns';

import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import subtask from '@/mixins/job/subtask';
import appointments from '@/mixins/job/appointments';
import JobListItemDetails from '@/views/Dashboard/components/JobListItemDetails';
import StatusBubble from '@/views/Dashboard/components/StatusBubble';
import SubStatusBubble from '@/views/Dashboard/components/SubStatusBubble';
import AppointmentBubble from '@/views/Dashboard/components/AppointmentBubble';
import GreenBubble from '@/views/Dashboard/components/GreenBubble';

export default {
  name: 'job-list-item',
  components: {
    JobListItemDetails,
    StatusBubble,
    SubStatusBubble,
    AppointmentBubble,
    GreenBubble,
  },
  mixins: [
    jobParams,
    job,
    subtask,
    appointments,
  ],
  props: {
    id: [String, Number],
  },
  computed: {
    // Used by job mixin.
    jobId () {
      return this.id;
    },
    // Used by appointment mixin.
    appointmentIds () {
      return this.match.appointments || [];
    },
    // Used by subtask mixin.
    subtaskId () {
      return this.job.subtask;
    },
    hasNotification () {
      return this.match.time_seen_pro === undefined && this.isNewMatch;
    },
    hasAppointment () {
      return !!this.appointments[0];
    },
    hasUpcomingAppointment () {
      return (
        this.hasAppointment
        && isAfter(
          new Date(this.appointments[0].end_dtm),
          Date.now()
        )
      );
    },
    time () {
      return this.isFinishedMatch ? this.match.time_updated : this.match.time_created;
    },
    isReadyToHire () {
      return (
        this.isNewStatusAlias || this.isPotentialStatusAlias)
        && this.urgency === this.$options.URGENCY_LABELS[1];
    },
    isOverdue () {
      return isAfter(new Date(Date.now()), new Date(this.timeToExpire))
        && this.$options.JOBS_STATUS_ALIASES.new.statuses.includes(this.match.status);
    },
    showSubStatus () {
      if (!this.isPotentialStatusAlias) {
        return true;
      }
      if (this.customJobsSubStatuses.length === 0) {
        return true;
      }
      if (!this.match.sub_status) {
        return this.customJobsSubStatuses.includes('Select Status');
      }

      return this.customJobsSubStatuses.includes(this.match.sub_status);
    },
  },
  watch: {
    match (prev, cur) {
      if (prev.sub_status !== cur.sub_status) {
        this.$emit('change');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";
  @import "~@/views/Dashboard/tools";

  .JOB-LIST-ITEM {
    @include padding-left(2, 2, 2);
    @include hover(text-decoration, none, none);
    @include desktop-hover(background-color, transparent, $color-gray20);
    @include pseudo(visited, color, $color-gray80, $color-gray80);
    display: flex;
    flex: 0 0 auto;
    min-height: $job-list-item-min-height;
    padding-top: 2*$unit;
    padding-right: 2*$unit;
    padding-bottom: 2*$unit;
    background-color: white;
    border-bottom: 1px solid $color-gray30;
    will-change: background-color;
    transition: background-color $transition-half-duration;

    &.ACTIVE {
      background-color: lighten($color-depot, 47%);
    }
  }

  .NOTIFICATION-BUBBLE {
    @include circle($unit);
    display: flex;
    margin: 1.25*$unit $unit 0 0;

    &.ACTIVE {
      background-color: $color-depot;
    }
  }

  .ICON {
    @include h6;
    margin-right: $unit/2;
  }

  .TIMER,
  .STATUS-BUBBLE {
    font-size: 11px ;
    text-transform: capitalize;
    margin-top: 0.75*$unit;
  }

  .TIMER {
    @include text-medium;
    display: flex;
    align-items: center;
    color: $color-depot;
  }
  .OVERDUE_BUBBLE {
     @include text-medium;
    display: flex;
    align-items: center;
    @include text-small;
    max-width: 100%;
    margin-top: 0.75*$unit;
    padding: $unit/4 0.75*$unit;
    align-self: flex-start;
    color: #CF1322;
    font-weight: 400;
    background-color: #FFF1F0;
    border: 1px solid #CF1322;
    border-radius:100px;

    &.ACTIVE {
      color: $color-red;
    }
  }
  .DARK-TEXT {
    color: #808286;
  }
</style>
